<template>
  <section class="card" :class="{ 'card--flush': flush, 'card--collapsed': collapsible && !open }">
    <header v-if="title || $slots.actions" class="card__head">
      <!-- Plegable. Ver la nota sobre `collapsible` en el script. -->
      <div v-if="collapsible" class="grow">
        <h3 v-if="title" class="card__title">
          <button
            type="button"
            class="card__toggle"
            :aria-expanded="String(open)"
            @click="open = !open"
          >
            {{ title }}
            <svg
              class="card__chevron" :class="{ 'is-open': open }"
              width="16" height="16" viewBox="0 0 24 24"
              fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true"
            ><polyline points="6 9 12 15 18 9" /></svg>
          </button>
        </h3>
        <p v-if="subtitle" class="card__subtitle">{{ subtitle }}</p>
      </div>

      <div v-else class="grow">
        <h3 v-if="title" class="card__title">{{ title }}</h3>
        <p v-if="subtitle" class="card__subtitle">{{ subtitle }}</p>
      </div>

      <div v-if="$slots.actions" class="row gap-2"><slot name="actions" /></div>
    </header>
    <div v-if="!collapsible || open" class="card__body"><slot /></div>
  </section>
</template>

<script setup>
import { ref } from 'vue'

const props = defineProps({
  title: String,
  subtitle: String,
  flush: Boolean,   // no body padding, for tables that fill the card

  /**
   * El cuerpo se pliega y la cabecera se convierte en el botón que lo abre.
   *
   * Existe para lo que una ficha tiene y casi nadie mira: los datos maestros de
   * un emplazamiento —cliente, dirección, metros— se teclean una vez y se leen
   * casi nunca, pero ocupan media pantalla por encima de lo que sí se viene a
   * ver. Plegado no es esconderlo: el título sigue ahí y dice qué hay dentro.
   *
   * Necesita `title`; sin él la cabecera no se dibuja y no habría dónde pulsar.
   *
   * Sobre el marcado: el botón va DENTRO del encabezado y no al revés. Es el
   * patrón de acordeón de ARIA, y además es lo único válido — un botón solo
   * admite contenido de frase, así que un encabezado metido dentro de él es
   * HTML roto que los navegadores dibujan igual y los lectores de pantalla no.
   * Las acciones de la tarjeta se quedan fuera del botón: un clic, una cosa.
   */
  collapsible: Boolean,

  /** Plegado de entrada, que es el caso por el que existe. */
  startOpen: Boolean,
})

// Sin persistir a propósito: cada ficha se abre igual que la anterior. Recordar
// la elección sería una preferencia por persona y por tarjeta, y eso es una
// decisión de producto que nadie ha tomado todavía.
const open = ref(props.startOpen)
</script>

<style scoped>
.card {
  /* A column, so the body can grow: a card sharing a grid row stretches to the
     tallest of its neighbours, and without this the body would keep its own
     height and leave the bottom of the card empty below it. */
  display: flex;
  flex-direction: column;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
  overflow: hidden;
}

.card__head {
  display: flex;
  align-items: center;
  gap: var(--space-4);
  padding: var(--space-4) var(--space-5);
  border-bottom: 1px solid var(--color-border);
}

/* El título como botón: sin piel de botón. Lo que dice que se puede pulsar es
   el galón y el cursor, no una caja gris alrededor del título de la tarjeta.
   Ancho completo para que el área pulsable sea la fila y no las doce letras del
   título — en un móvil eso es la diferencia entre acertar y no acertar. */
.card__toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  width: 100%;
  padding: 0;
  background: none;
  border: 0;
  font: inherit;
  color: inherit;
  text-align: left;
  cursor: pointer;
}
.card__toggle:hover { color: var(--color-primary); }
/* Con el dedo la fila medía 20 px de alto: se agranda lo que se toca, no lo
   que se ve, para no mover la cabecera de cada tarjeta plegable. */
@media (pointer: coarse) {
  .card__toggle { position: relative; }
  .card__toggle::after { content: ''; position: absolute; inset: -12px 0; }
}
.card__toggle:focus-visible { outline: 2px solid var(--color-primary); outline-offset: 4px; border-radius: 2px; }

.card__chevron { flex: none; color: var(--color-text-mute); transition: transform 0.15s ease; }
.card__chevron.is-open { transform: rotate(180deg); }

/* Plegada, la cabecera es lo último de la tarjeta: su raya de abajo se sumaría
   al borde y se verían dos líneas pegadas. */
.card--collapsed .card__head { border-bottom: none; }

.card__title { font-size: var(--text-lg); font-weight: 600; }
.card__subtitle { font-size: var(--text-sm); color: var(--color-text-soft); margin-top: 3px; }

/* Slightly more room than the header: content wants breathing space on all
   four sides, a title only needs it above and below. */
.card__body { flex: 1; padding: var(--space-5) var(--space-5) var(--space-6); }

/* Direct child, and the `>` is the whole point: as a descendant selector this
   made `flush` contagious. A flush card holding a table is the normal case, but
   a flush card holding a COMPONENT that draws its own card — a related panel
   under a strip of tabs — flattened that one too, and its contents came out
   welded to the outer card's border with the dates cut off at the edge.
   `flush` means "this card's own body", never "every card body beneath me". */
.card--flush > .card__body { padding: 0; }
</style>
